import UnifiedSignIn from '@/pages/UnifiedSignIn';
import { BrowserRouter, Routes, Route, Outlet, useLocation, Navigate } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { useNavigationType } from 'react-router-dom';
import '@/App.css';
import Navbar from '@/components/Navbar';
import PmbSessionNotifier from '@/components/PmbSessionNotifier';
import Footer from '@/components/Footer';
import ContentProtection from '@/components/ContentProtection';
import BackToTopButton from '@/components/BackToTopButton';
import KalaManchAssistant from '@/components/KalaManchAssistant';
import Home from '@/pages/Home';
import Artists from '@/pages/Artists';
import ArtistProfile from '@/pages/ArtistProfileReal';
import ArtistInbox from '@/pages/ArtistInbox';
import MyAccount from '@/pages/MyAccount';
import EmailVerified from '@/pages/EmailVerified';
import About from '@/pages/About';
import Contact from '@/pages/Contact';
import Collaborators from '@/pages/Collaborators';
import CollaboratorProfile from '@/pages/CollaboratorProfile';
import CollaboratorAccount from '@/pages/CollaboratorAccount';
import CollaboratorInbox from '@/pages/CollaboratorInbox';
import ForgotPassword from '@/pages/ForgotPassword';
import FAQ from '@/pages/FAQ';
import GovernmentInstitution from '@/pages/GovernmentInstitution';
import MusicLabel from '@/pages/MusicLabel';
import ProductionHouse from '@/pages/ProductionHouse';
import TheaterGroup from '@/pages/TheaterGroup';
import ArtsFoundation from '@/pages/ArtsFoundation';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsAndConditions from '@/pages/TermsAndConditions';
import TrustSafety from '@/pages/TrustSafety';
import ChildSafetyStandards from '@/pages/ChildSafetyStandards';
import AccountDeletion from '@/pages/AccountDeletion';
import AiStudio from '@/pages/AiStudio';
import AiManager from '@/pages/AiManager';
import AiMentor from '@/pages/AiMentor';
import ArtistEpk from '@/pages/ArtistEpk';
import EventPlanner from '@/pages/EventPlanner';
import CollabMatch from '@/pages/CollabMatch';
import MobileAppPage from '@/pages/MobileAppPage';
import MediaKitPage from '@/pages/MediaKitPage';
import IndianArtPage from '@/pages/IndianArtPage';
import IndianArtStarProfile from '@/pages/IndianArtStarProfile';
import ArtExhibition from '@/pages/ArtExhibition';
import Marketplace from '@/pages/Marketplace';
import MarketplaceAccount from '@/pages/MarketplaceAccount';
import MarketplaceProductDetail from '@/pages/MarketplaceProductDetail';
import Learn from '@/pages/Learn';
import Competitions from '@/pages/Competitions';
import CompetitionDetail from '@/pages/CompetitionDetail';
import Projects from '@/pages/Projects';
import LiveAuditions from '@/pages/LiveAuditions';
import CastingPost from '@/pages/CastingPost';
import CastingResults from '@/pages/CastingResults';
import ArtistMatches from '@/pages/ArtistMatches';
import ExhibitionWall from '@/pages/ExhibitionWall';
import ProtectedArtistRoute from '@/components/ProtectedArtistRoute';
import ProtectedCollaboratorRoute from '@/components/ProtectedCollaboratorRoute';
import { ArtistAuthProvider } from '@/context/ArtistAuthContext';
import { CollaboratorAuthProvider } from '@/context/CollaboratorAuthContext';
import { BuyerAuthProvider } from '@/context/BuyerAuthContext';
import { LanguageProvider } from '@/context/LanguageContext';
import ProtectedAdminRoute from '@/admin/ProtectedAdminRoute';
import AdminLayout from '@/admin/AdminLayout';
import AdminLogin from '@/admin/pages/AdminLogin';
import AdminDashboard from '@/admin/pages/AdminDashboard';
import AdminArtists from '@/admin/pages/AdminArtists';
import AdminCollaborators from '@/admin/pages/AdminCollaborators';
import AdminVideos from '@/admin/pages/AdminVideos';
import AdminSettings from '@/admin/pages/AdminSettings';
import AdminSupportMessages from '@/admin/pages/AdminSupportMessages';
import AdminSupport from '@/admin/pages/AdminSupport';
import AdminEnquiries from '@/admin/pages/AdminEnquiries';
import AdminSponsors from '@/admin/pages/AdminSponsors';
import AdminSuccessStories from '@/admin/pages/AdminSuccessStories';
import AdminArtistLeads from '@/admin/pages/AdminArtistLeads';
import AdminAiInsights from '@/admin/pages/AdminAiInsights';
import AdminAiStudio from '@/admin/pages/AdminAiStudio';
import AdminAiMentor from '@/admin/pages/AdminAiMentor';
import AdminKalaOps from '@/admin/pages/AdminKalaOps';
import AdminTrustQueue from '@/admin/pages/AdminTrustQueue';
import AdminLivingEpk from '@/admin/pages/AdminLivingEpk';
import AdminIndianArtStars from '@/admin/pages/AdminIndianArtStars';
import AdminArtExhibition from '@/admin/pages/AdminArtExhibition';
import AdminMarketplace from '@/admin/pages/AdminMarketplace';
import AdminLearn from '@/admin/pages/AdminLearn';
import AdminLearnCosts from '@/admin/pages/AdminLearnCosts';
import AdminContentSeo from '@/admin/pages/AdminContentSeo';
import AdminAlerts from '@/admin/pages/AdminAlerts';
import AdminCompetitions from '@/admin/pages/AdminCompetitions';
import AdminProjects from '@/admin/pages/AdminProjects';
import AdminLiveAuditions from '@/admin/pages/AdminLiveAuditions';
import AdminBroadcasts from '@/admin/pages/AdminBroadcasts';

function ScrollManager() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const lastPathRef = useRef('');

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    const storageKey = `scroll:${location.pathname}${location.search}${location.hash}`;
    const saveScroll = () => {
      sessionStorage.setItem(storageKey, String(window.scrollY || 0));
    };
    const handleScroll = () => {
      window.clearTimeout(window.__kalamanchScrollSaveTimer);
      window.__kalamanchScrollSaveTimer = window.setTimeout(saveScroll, 120);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      saveScroll();
    };
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const pathKey = `${location.pathname}${location.search}${location.hash}`;
    if (lastPathRef.current === pathKey) return;
    lastPathRef.current = pathKey;

    if (navigationType === 'POP') {
      const saved = Number(sessionStorage.getItem(`scroll:${pathKey}`) || 0);
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: saved, left: 0, behavior: 'auto' });
      });
      return;
    }

    if (location.hash) {
      const targetId = decodeURIComponent(location.hash.slice(1));
      window.setTimeout(() => {
        const target = document.getElementById(targetId);
        if (target) {
          target.scrollIntoView({ behavior: 'auto', block: 'start' });
        } else {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
      }, 120);
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: 'auto' }));
    window.setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: 'auto' }), 160);
  }, [location.pathname, location.search, location.hash, navigationType]);

  return null;
}

function PublicLayout() {
  return (
    <>
      <ContentProtection />
      <Navbar />
      <PmbSessionNotifier />
      <Outlet />
      <KalaManchAssistant />
      <BackToTopButton />
      <Footer />
    </>
  );
}

function HomeOrSubdomainApp() {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  if (hostname === 'market.kalamanch.art') return <Marketplace />;
  if (hostname === 'learn.kalamanch.art') return <Learn />;
  return <Home />;
}

function HostRedirectGuard() {
  const location = useLocation();
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  useEffect(() => {
    const path = `${location.pathname}${location.search}${location.hash}`;
    if (hostname === 'market.kalamanch.art') {
      if (location.pathname === '/' || location.pathname.startsWith('/marketplace/') || location.pathname === '/marketplace') return;
      window.location.replace(`https://www.kalamanch.art${path}`);
    }
    if (hostname === 'learn.kalamanch.art') {
      if (location.pathname === '/') return;
      window.location.replace(`https://www.kalamanch.art${path}`);
    }
  }, [hostname, location.hash, location.pathname, location.search]);
  return null;
}

function App() {
  return (
    <div className="App">
      <ArtistAuthProvider>
        <CollaboratorAuthProvider>
          <BuyerAuthProvider>
            <LanguageProvider>
              <BrowserRouter>
              <ScrollManager />
              <HostRedirectGuard />
              <Routes>
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route element={<ProtectedAdminRoute />}>
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="artists" element={<AdminArtists />} />
                    <Route path="alerts" element={<AdminAlerts />} />
                    <Route path="collaborators" element={<AdminCollaborators />} />
                    <Route path="videos" element={<AdminVideos />} />
                    <Route path="support" element={<AdminSupportMessages />} />
                    <Route path="support-content" element={<AdminSupport />} />
                    <Route path="enquiries" element={<AdminEnquiries />} />
                    <Route path="sponsors" element={<AdminSponsors />} />
                    <Route path="success-stories" element={<AdminSuccessStories />} />
                    <Route path="competitions" element={<AdminCompetitions />} />
                    <Route path="live-auditions" element={<AdminLiveAuditions />} />
                    <Route path="projects" element={<AdminProjects />} />
                    <Route path="artist-leads" element={<AdminArtistLeads />} />
                    <Route path="broadcasts" element={<AdminBroadcasts />} />
                    <Route path="ai-insights" element={<AdminAiInsights />} />
                    <Route path="ai-studio" element={<AdminAiStudio />} />
                    <Route path="ai-mentor" element={<AdminAiMentor />} />
                    <Route path="kalaops" element={<Navigate to="overview" replace />} />
                    <Route path="kalaops/:section" element={<AdminKalaOps />} />
                    <Route path="trust" element={<AdminTrustQueue />} />
                    <Route path="living-epk" element={<AdminLivingEpk />} />
                    <Route path="art-exhibition" element={<AdminArtExhibition />} />
                    <Route path="marketplace" element={<Navigate to="products" replace />} />
                    <Route path="marketplace/products" element={<AdminMarketplace section="products" />} />
                    <Route path="marketplace/orders" element={<AdminMarketplace section="orders" />} />
                    <Route path="learn" element={<AdminLearn />} />
                    <Route path="learn/costs" element={<AdminLearnCosts />} />
                    <Route path="indian-art-stars" element={<AdminIndianArtStars />} />
                    <Route path="content-seo" element={<AdminContentSeo />} />
                    <Route path="settings" element={<AdminSettings />} />
                  </Route>
                </Route>
                <Route element={<PublicLayout />}>
                  <Route path="/" element={<HomeOrSubdomainApp />} />
                  <Route path="/artists" element={<Artists />} />
                  <Route path="/artist/:id" element={<ArtistProfile />} />
                  <Route path="/epk/:slug" element={<ArtistEpk />} />
                  <Route path="/collaborators" element={<Collaborators />} />
                  <Route path="/ai-studio" element={<AiStudio />} />
                  <Route path="/ai-manager" element={<AiManager />} />
                  <Route path="/ai-mentor" element={<AiMentor />} />
                  <Route path="/kala-ai-guru" element={<Navigate to="/ai-mentor" replace />} />
                  <Route path="/event-planner" element={<EventPlanner />} />
                  <Route path="/collab-match" element={<CollabMatch />} />
                  <Route path="/competitions" element={<Competitions />} />
                  <Route path="/competitions/:id" element={<CompetitionDetail />} />
                  <Route path="/live-auditions" element={<LiveAuditions />} />
                  <Route path="/projects" element={<Projects />} />
                  <Route path="/casting/post" element={<CastingPost />} />
                  <Route path="/casting/results/:briefId" element={<CastingResults />} />
                  <Route path="/mobile-app" element={<MobileAppPage />} />
                  <Route path="/media-kit" element={<MediaKitPage />} />
                  <Route path="/indian-art" element={<IndianArtPage />} />
                  <Route path="/indian-art/stars/:slug" element={<IndianArtStarProfile />} />
                  <Route path="/art-exhibition" element={<ArtExhibition />} />
                  <Route path="/sign-in" element={<UnifiedSignIn />} />
                    <Route path="/marketplace/account" element={<MarketplaceAccount />} />
                  <Route path="/marketplace" element={<Marketplace />} />
                  <Route path="/marketplace/product/:productId" element={<MarketplaceProductDetail />} />
                  <Route path="/learn/*" element={<Learn />} />
                  <Route path="/collaborator/:id" element={<CollaboratorProfile />} />
                  <Route path="/email-verified" element={<EmailVerified />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/partners/government-institution" element={<GovernmentInstitution />} />
                  <Route path="/partners/music-label" element={<MusicLabel />} />
                  <Route path="/partners/production-house" element={<ProductionHouse />} />
                  <Route path="/partners/theater-group" element={<TheaterGroup />} />
                  <Route path="/partners/arts-foundation" element={<ArtsFoundation />} />
                  <Route path="/faqs" element={<FAQ />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/account-deletion" element={<AccountDeletion />} />
                  <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                  <Route path="/terms" element={<TermsAndConditions />} />
                  <Route path="/trust-safety" element={<TrustSafety />} />
                  <Route path="/child-safety-standards" element={<ChildSafetyStandards />} />
                  <Route element={<ProtectedArtistRoute />}>
                    <Route path="/my-account" element={<MyAccount />} />
                    <Route path="/artist/inbox" element={<ArtistInbox />} />
                    <Route path="/artist/matches" element={<ArtistMatches />} />
                    <Route path="/tools/exhibition-wall" element={<ExhibitionWall />} />
                  </Route>
                  <Route element={<ProtectedCollaboratorRoute />}>
                    <Route path="/collaborator/account" element={<CollaboratorAccount />} />
                    <Route path="/collaborator/inbox" element={<CollaboratorInbox />} />
                  </Route>
                </Route>
              </Routes>
              </BrowserRouter>
            </LanguageProvider>
          </BuyerAuthProvider>
        </CollaboratorAuthProvider>
      </ArtistAuthProvider>
    </div>
  );
}

export default App;

